/**
 * Utility for processing API request/response component outputs
 * Handles legacy data structures and formats API responses for better display
 */

/**
 * Processes API request/response component data to remove legacy fields
 * and format the data in a more user-friendly way
 *
 * For inputs: Only removes legacy fields when setupMode is 'CURL', otherwise shows all fields
 * For outputs: Always processes to flatten structure and remove redundant data
 *
 * @param {Object} data - The raw component data
 * @param {Object} component - The component object containing type and other metadata
 * @param {string} section - The section being processed ('inputs', 'outputs', etc.)
 * @returns {Object} - Processed data with conditional legacy field removal and better structure
 */
export const processApiComponentOutput = (data, component, section = 'outputs') => {
  // Only process API request/response components
  if (component?.type !== 'api_request_and_response') {
    return data;
  }

  // Handle inputs section - remove legacy configuration fields only if setupMode is CURL
  if (section === 'inputs' && data && typeof data === 'object') {
    // Check if this looks like API input configuration
    const legacyFields = [
      'request_type',
      'data_type',
      'endpoint_url',
      'body',
      'headers',
      'skipSample',
    ];
    const hasLegacyFields = legacyFields.some(field => data.hasOwnProperty(field));

    // Only process if it has legacy fields AND setupMode is CURL
    if (hasLegacyFields && data.setupMode === 'CURL') {
      // Keep only the useful fields for CURL-based inputs
      const processedData = {};

      // Keep curl command as it's the most useful for users
      if (data.curl) {
        processedData.curl = data.curl;
      }

      // Keep setup mode as it provides context
      if (data.setupMode) {
        processedData.setupMode = data.setupMode;
      }

      // Keep the response placeholder name as it's useful for referencing
      if (data.rawResponsePlaceholderName) {
        processedData.responseVariable = data.rawResponsePlaceholderName;
      }

      // If there are no useful fields, show a simplified message
      if (Object.keys(processedData).length === 0) {
        return { note: 'API request configuration (legacy fields hidden)' };
      }

      return processedData;
    }

    // If setupMode is not CURL, return original data to show all legacy fields
    // This is useful for form-based API configuration where users need to see all fields
    return data;
  }

  // Handle outputs section - process API response data
  if (
    section === 'outputs' &&
    data &&
    typeof data === 'object' &&
    data.responseData &&
    data.requestData
  ) {
    const requestData = data.requestData;

    // If requestData has responseBody, extract and flatten it
    if (requestData && requestData.responseBody) {
      const responseBody = requestData.responseBody;

      // Try to parse JSON strings in the response body
      let parsedResponse = { ...responseBody };
      Object.keys(responseBody).forEach(key => {
        if (typeof responseBody[key] === 'string') {
          try {
            // Attempt to parse JSON strings in the response
            parsedResponse[key] = JSON.parse(responseBody[key]);
          } catch (e) {
            // Keep as string if parsing fails
            parsedResponse[key] = responseBody[key];
          }
        }
      });

      // Return flattened structure with response data at the top level
      // Keep status information for context
      const processedData = {
        ...parsedResponse,
        // Add meta information about the request
        _meta: {
          status: requestData.status || parsedResponse.status,
          headers: parsedResponse.headers,
        },
      };

      // Remove redundant status and headers from the main level if they exist in meta
      if (processedData.status && processedData._meta.status) {
        delete processedData.status;
      }
      if (processedData.headers && processedData._meta.headers) {
        delete processedData.headers;
      }

      return processedData;
    }

    // If no responseBody, just return the requestData without the wrapper
    return requestData;
  }

  // Return original data if it doesn't match the expected structure
  return data;
};

/**
 * Checks if a component is an API request/response component
 *
 * @param {Object} component - The component object
 * @returns {boolean} - True if it's an API component
 */
export const isApiComponent = component => {
  return component?.type === 'api_request_and_response';
};

/**
 * Extracts the main API response data from processed component output
 *
 * @param {Object} processedData - Already processed component data
 * @returns {Object} - The main response data or empty object
 */
export const extractApiResponseData = processedData => {
  // With the new flattened structure, the response data is at the top level
  // excluding the _meta field
  if (processedData && typeof processedData === 'object') {
    const { _meta, ...responseData } = processedData;
    return responseData;
  }
  return processedData || {};
};
